import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@bookdental/ui";
import { useAuth } from "@/lib/auth";
import { useSwitchOrganization } from "@/lib/use-switch-organization";
import { ProsthesisRequestForm } from "./ProsthesisRequestForm";
import { NewOrderStartDialog, type OrderStart, type ServicoInicial } from "./NewOrderStartDialog";
import type { ProsthesisOrderRow } from "./types";

/**
 * Novo pedido (paciente + laboratório → formulário) e edição de pedido, como usados na tela de
 * Prótese e no painel de /organizacoes. `elementos` são os diálogos; quem usa só os renderiza.
 */
export function useFluxoNovoPedido({ onConcluido }: { onConcluido?: () => void } = {}) {
  const { user } = useAuth();
  const { switchTo } = useSwitchOrganization();
  const [orderStart, setOrderStart] = useState<OrderStart | null>(null);
  const [pedidoEmEdicao, setPedidoEmEdicao] = useState<ProsthesisOrderRow | null>(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  // Aberto a partir de um item do plano de tratamento: paciente travado, dentes e vínculo do item.
  const [doPlano, setDoPlano] = useState<{ patient: { id: string; name: string }; itemId: string | null; dentes: string | null } | null>(null);
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  // Aberto pelo catálogo público: laboratório (e, de uma linha, serviço e material) já escolhidos.
  const [preEscolha, setPreEscolha] = useState<{ labId: string; servico?: ServicoInicial } | null>(null);
  // Organização ativa antes de abrir o fluxo — se o seletor de organização (nos passos 1/2) trocar
  // a org e o fluxo for cancelado sem criar o pedido, volta pra essa aqui.
  const [pedidoInitialOrgId, setPedidoInitialOrgId] = useState<string | null>(null);

  function revertPedidoOrgIfNeeded() {
    if (pedidoInitialOrgId && user?.activeOrganization?.id !== pedidoInitialOrgId) {
      switchTo(pedidoInitialOrgId).catch(() => {});
    }
    setPedidoInitialOrgId(null);
  }

  function abrirNovo() {
    setDoPlano(null);
    setPreEscolha(null);
    setPedidoInitialOrgId(user?.activeOrganization?.id ?? null);
    setStartDialogOpen(true);
  }

  function abrirDoPlano(dados: { patient: { id: string; name: string }; itemId: string | null; dentes: string | null }) {
    setDoPlano(dados);
    setPreEscolha(null);
    setPedidoInitialOrgId(user?.activeOrganization?.id ?? null);
    setStartDialogOpen(true);
  }

  // Reabre o formulário em cima do pedido: paciente e laboratório vêm dele e ficam travados,
  // igual ao fluxo de criação depois do primeiro passo.
  function editar(order: ProsthesisOrderRow) {
    setPedidoEmEdicao(order);
    setOrderStart({
      patient: { id: order.patientId, name: order.patientName, phone: "" },
      labId: order.labId ?? "",
    });
    setOrderFormOpen(true);
  }

  function abrirComLaboratorio(dados: { labId: string; servico?: ServicoInicial }) {
    setDoPlano(null);
    setPreEscolha(dados);
    setPedidoInitialOrgId(user?.activeOrganization?.id ?? null);
    setStartDialogOpen(true);
  }

  function concluido() {
    setOrderFormOpen(false);
    setOrderStart(null);
    setPedidoEmEdicao(null);
    setPedidoInitialOrgId(null);
    setDoPlano(null);
    setPreEscolha(null);
    onConcluido?.();
  }

  const elementos = (
    <>
      <NewOrderStartDialog
        // Remonta ao trocar o paciente fixo: o estado inicial do diálogo vem dele.
        key={doPlano ? (doPlano.itemId ?? doPlano.patient.id) : (preEscolha?.labId ?? "livre")}
        open={startDialogOpen}
        pacienteFixo={doPlano?.patient}
        labInicial={preEscolha?.labId}
        onClose={() => {
          setStartDialogOpen(false);
          revertPedidoOrgIfNeeded();
        }}
        onConfirm={(start) => {
          setOrderStart({
            ...start,
            ...(doPlano?.itemId ? { doPlano: { itemId: doPlano.itemId, dentes: doPlano.dentes } } : {}),
            // O serviço da linha só vale se o laboratório continuou o do catálogo.
            ...(preEscolha?.servico && start.labId === preEscolha.labId ? { servico: preEscolha.servico } : {}),
          });
          setStartDialogOpen(false);
          setOrderFormOpen(true);
        }}
      />

      <Dialog
        open={orderFormOpen}
        onOpenChange={(v) => {
          if (!v) {
            setOrderFormOpen(false);
            setPedidoEmEdicao(null);
            revertPedidoOrgIfNeeded();
          }
        }}
      >
        <DialogContent className="max-w-[1400px] max-h-[90vh] overflow-y-auto dark:bg-card">
          <DialogTitle className="sr-only">{pedidoEmEdicao ? "Editar pedido" : "Novo pedido"}</DialogTitle>
          {orderStart && (
            <ProsthesisRequestForm
              // Chave pelo pedido: cada edição monta o formulário do zero com os dados daquele
              // pedido, em vez de herdar estado do anterior.
              key={pedidoEmEdicao?.id ?? "novo"}
              start={orderStart}
              onCreated={concluido}
              editing={pedidoEmEdicao ?? undefined}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  /** Abrir o fluxo a partir de um link (`?novo=1`). */
  const abrirInicio = () => setStartDialogOpen(true);

  /** Da ficha do paciente: o paciente já vem travado, só falta o laboratório. */
  const abrirParaPaciente = (patient: { id: string; name: string }) => abrirDoPlano({ patient, itemId: null, dentes: null });

  return { abrirNovo, abrirInicio, abrirDoPlano, abrirParaPaciente, abrirComLaboratorio, editar, elementos };
}
